/**
 * Provider stack.
 *
 * Order matters: AuthProvider sits INSIDE the router, because the guards call
 * both `useAuth` and `useNavigate`/`useLocation`.
 *
 * `BrowserRouter` + `useRoutes`, not `createBrowserRouter`. The data router's
 * loader/action machinery is unused here — server state is TanStack Query's job,
 * not the router's — and its navigation path constructs a `Request`, which is
 * unusable under jsdom (undici's `Request` rejects jsdom's `AbortSignal`), so it
 * would force the router tests to exercise a different mechanism than
 * production. If loaders are ever wanted, switch both this file and the test
 * harness together.
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, useRoutes } from 'react-router-dom';
import { queryClient } from './lib/queryClient';
import { routes } from './routes/router';
import { AuthProvider } from './auth/AuthContext';
import { ThemeProvider } from './theme/ThemeContext';
import { CarbonThemeBridge } from './theme/CarbonThemeBridge';

function RouteTree(): React.JSX.Element | null {
  return useRoutes(routes);
}

export function App(): React.JSX.Element {
  return (
    <ThemeProvider>
      <CarbonThemeBridge>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <RouteTree />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </CarbonThemeBridge>
    </ThemeProvider>
  );
}

export default App;
